import { AlertCircle, CheckCircle2, Clock3, DatabaseZap } from 'lucide-react';

import type { TrafficEvidenceAttempt, TrafficEvidenceRun } from '@/services/api';
import {
  evidenceAttemptHeading,
  evidenceCountLabel,
  evidenceRunHeading,
  evidenceSourceRevisionLabel,
  normalizeEvidenceTimestamp,
} from '@/components/trafficEvidenceRunPresentation';

function timeLabel(value: string | null): string {
  if (!value) return 'Unavailable';
  const date = new Date(normalizeEvidenceTimestamp(value));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function outcomeLabel(attempt: TrafficEvidenceAttempt): string {
  if (attempt.outcome === 'succeeded') return 'Succeeded';
  if (attempt.outcome === 'failed') return 'Failed';
  if (attempt.outcome === 'interrupted') return 'Interrupted';
  return 'Pending';
}

function AttemptRow({ attempt }: { attempt: TrafficEvidenceAttempt }) {
  return (
    <li className="rounded-md border border-surface-200 bg-surface-50 p-2.5 dark:border-surface-700 dark:bg-surface-950">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-[11px] font-semibold text-surface-800 dark:text-surface-100">
          {evidenceAttemptHeading(attempt)}
        </p>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-surface-500 dark:text-surface-400">
          {outcomeLabel(attempt)}
        </span>
      </div>
      <dl className="mt-2 grid gap-x-4 gap-y-1 text-[10px] sm:grid-cols-2">
        <div><dt className="inline text-surface-500 dark:text-surface-400">Record IDs: </dt><dd className="inline font-mono text-surface-700 dark:text-surface-200">{attempt.providerRecordIds.join(', ') || 'None'}</dd></div>
        <div><dt className="inline text-surface-500 dark:text-surface-400">Batch ID: </dt><dd className="inline font-mono text-surface-700 dark:text-surface-200">{attempt.deterministicBatchId}</dd></div>
        <div><dt className="inline text-surface-500 dark:text-surface-400">Started: </dt><dd className="inline text-surface-700 dark:text-surface-200">{timeLabel(attempt.startedAt)}</dd></div>
        <div><dt className="inline text-surface-500 dark:text-surface-400">Completed: </dt><dd className="inline text-surface-700 dark:text-surface-200">{timeLabel(attempt.completedAt)}</dd></div>
      </dl>
      {attempt.failure && (
        <p className="mt-2 text-[10px] leading-4 text-red-700 dark:text-red-300">
          {attempt.failure.message} <span className="font-mono">({attempt.failure.code})</span>
        </p>
      )}
    </li>
  );
}

function RunDetails({ run, open }: { run: TrafficEvidenceRun; open: boolean }) {
  return (
    <details open={open} className="rounded-lg border border-surface-200 bg-white dark:border-surface-800 dark:bg-surface-900">
      <summary className="cursor-pointer list-none px-3 py-2.5 text-xs font-semibold text-surface-900 dark:text-white">
        <span>{evidenceRunHeading(run)}</span>
        <span className="ml-2 font-mono text-[10px] font-normal text-surface-500 dark:text-surface-400">{run.id}</span>
      </summary>
      <div className="border-t border-surface-200 p-3 dark:border-surface-800">
        {run.failure && (
          <div role="alert" className="mb-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-2.5 text-[11px] leading-4 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{run.failure.message} <span className="font-mono">({run.failure.code})</span></span>
          </div>
        )}

        <dl className="grid gap-3 text-[11px] sm:grid-cols-2 lg:grid-cols-3">
          <div className="min-w-0"><dt className="text-surface-500 dark:text-surface-400">Source</dt><dd className="mt-0.5 break-all font-medium text-surface-800 dark:text-surface-100">{run.source.connectorKey} · {run.source.capabilityId} · {run.source.dataset}</dd></div>
          <div className="min-w-0"><dt className="text-surface-500 dark:text-surface-400">Source revision</dt><dd className="mt-0.5 break-all font-mono text-surface-800 dark:text-surface-100" title={run.source.sourceRevision}>{run.source.sourceRevision}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Request correlation</dt><dd className="mt-0.5 break-all font-mono text-surface-800 dark:text-surface-100">{run.requestCorrelationId}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Comparison period</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">{run.periods.comparison.startDate}–{run.periods.comparison.endDate}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Current period</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">{run.periods.current.startDate}–{run.periods.current.endDate}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Requested records</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">{evidenceCountLabel(run.requestedRecordCounts.customers)} Customer · {evidenceCountLabel(run.requestedRecordCounts.providers)} Provider</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Provider requests</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">{evidenceCountLabel(run.actualRequestCount)} actual · {evidenceCountLabel(run.estimatedRequestCount)} estimated</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Retries</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">{evidenceCountLabel(run.retryCount)}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Mapping snapshot</dt><dd className="mt-0.5 break-all font-mono text-surface-800 dark:text-surface-100">{run.mappingSnapshotId}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Started</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">{timeLabel(run.startedAt)}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Completed</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">{timeLabel(run.completedAt)}</dd></div>
          <div><dt className="text-surface-500 dark:text-surface-400">Schema</dt><dd className="mt-0.5 text-surface-800 dark:text-surface-100">Evidence v{run.source.evidenceSchemaVersion} · Contract v{run.source.invocationContractVersion}</dd></div>
        </dl>

        {run.warnings.length > 0 && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 dark:border-amber-900 dark:bg-amber-950/25">
            <p className="text-[11px] font-semibold text-amber-900 dark:text-amber-200">Warnings</p>
            <ul className="mt-1 space-y-1 text-[10px] text-amber-800 dark:text-amber-300">
              {run.warnings.map((warning) => <li key={warning}>• {warning}</li>)}
            </ul>
          </div>
        )}

        <div className="mt-3">
          <p className="text-[11px] font-semibold text-surface-900 dark:text-white">Started request history</p>
          {run.attempts.length === 0 ? (
            <p className="mt-1 text-[10px] text-surface-500 dark:text-surface-400">No provider request started.</p>
          ) : (
            <ul className="mt-2 space-y-2">{run.attempts.map((attempt) => <AttemptRow key={attempt.id} attempt={attempt} />)}</ul>
          )}
        </div>
      </div>
    </details>
  );
}

export function TrafficEvidenceRunHistory({
  runs,
  legacy,
}: {
  runs: TrafficEvidenceRun[];
  legacy: boolean;
}) {
  if (legacy) {
    return (
      <section className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/25">
        <div className="flex items-start gap-2">
          <Clock3 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-300" />
          <div>
            <h4 className="text-xs font-semibold text-amber-900 dark:text-amber-200">Legacy evidence</h4>
            <p className="mt-1 text-[11px] leading-4 text-amber-800 dark:text-amber-300">This case predates Evidence Run attribution. Source revision, request counts, retry counts, and request history are unavailable.</p>
          </div>
        </div>
      </section>
    );
  }

  if (runs.length === 0) return null;

  return (
    <section className="mt-3" aria-label="Evidence Run history">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <DatabaseZap className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
          <h4 className="text-xs font-semibold text-surface-900 dark:text-white">Evidence Run history</h4>
        </div>
        <p className="text-[10px] text-surface-500 dark:text-surface-400">
          {runs.length} immutable {runs.length === 1 ? 'run' : 'runs'} · Latest source {evidenceSourceRevisionLabel(runs[runs.length - 1]?.source.sourceRevision ?? null)}
        </p>
      </div>
      <div className="space-y-2">
        {runs.map((run, index) => (
          <RunDetails key={run.id} run={run} open={index === runs.length - 1} />
        ))}
      </div>
      {runs.some((run) => run.status === 'complete') && (
        <p className="mt-2 flex items-center gap-1.5 text-[10px] text-emerald-700 dark:text-emerald-300"><CheckCircle2 className="h-3 w-3" />Successful evidence does not replace earlier failed or interrupted runs.</p>
      )}
    </section>
  );
}
